import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "@/components/feature/AnimatedTitle";

const services = [
  {
    num: "01",
    title: "ホームページ制作",
    titleEn: "Web Design",
    desc: "事業目標・ターゲット・競合を分析し、コーディングからCMS構築まで一貫制作。高品質なWebサイトで、第一印象から信頼を獲得します。",
  },
  {
    num: "02",
    title: "SEO / MEO対策",
    titleEn: "SEO / MEO",
    desc: "データに基づいた戦略で、検索上位表示・Googleマップ上位表示を実現。集客の仕組みを内製化し、長期的な安定した流入を生み出します。",
  },
  {
    num: "03",
    title: "LLMO対策",
    titleEn: "LLMO",
    desc: "ChatGPT・Gemini・Claudeなど生成AIに選ばれるコンテンツ設計。AI検索時代に備え、新しい集客チャネルを今から構築します。",
  },
  {
    num: "04",
    title: "SNS / 広告運用",
    titleEn: "Digital Media",
    desc: "Instagram・X・TikTokのSNS運用から、リスティング広告・Meta広告まで。認知から興味、購入までの全ファネルを戦略的に設計します。",
  },
];

/* ── Reveal hook ── */
function useReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const winH = window.innerHeight;
    if (rect.top < winH && rect.bottom > 0) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -50px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── Service Card ── */
function ServiceCard({
  num,
  title,
  titleEn,
  desc,
  idx,
}: {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  idx: number;
}) {
  const navigate = useNavigate();
  const { ref, visible } = useReveal(0.1);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer flex flex-col transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${idx * 0.1}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${idx * 0.1}s`,
      }}
      onClick={() => navigate("/service")}
    >
      {/* ── Card Header ── */}
      <div
        className="flex items-center justify-between px-5 py-4 rounded-t-xl"
        style={{
          background: "#ede0de",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div className="flex items-baseline gap-2">
          <span
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              color: "#c00000",
              lineHeight: 1,
            }}
          >
            {num}
          </span>
          <div className="flex flex-col">
            <span
              style={{
                fontSize: "10px",
                fontWeight: 600,
                color: "#c00000",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}
            >
              Service:
            </span>
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#111",
                letterSpacing: "0.04em",
                marginTop: "1px",
              }}
            >
              {titleEn}
            </span>
          </div>
        </div>

        {/* Arrow icon */}
        <span
          className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300"
          style={{
            background: hovered ? "#c00000" : "rgba(0,0,0,0.06)",
            color: hovered ? "#fff" : "#c00000",
          }}
        >
          <i className="ri-arrow-right-up-line" style={{ fontSize: "16px" }} />
        </span>
      </div>

      {/* ── Card Body ── */}
      <div
        className="flex flex-col px-5 pt-6 pb-5 rounded-b-xl"
        style={{
          background: "#ffffff",
          border: "1px solid rgba(0,0,0,0.06)",
          borderTop: "none",
        }}
      >
        <h3
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontSize: "17px",
            fontWeight: 700,
            color: "#111",
            lineHeight: 1.5,
            marginBottom: "10px",
          }}
        >
          {title}
        </h3>
        <p
          className="flex-1 mb-5"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontSize: "13px",
            color: "#555",
            lineHeight: 1.9,
          }}
        >
          {desc}
        </p>

        {/* VIEW MORE button */}
        <button
          className="flex items-center gap-2 text-xs font-semibold whitespace-nowrap transition-all duration-300 self-start"
          style={{
            fontFamily: "'Inter', sans-serif",
            color: hovered ? "#c00000" : "#999",
            letterSpacing: "0.08em",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          VIEW MORE
          <i
            className="ri-arrow-right-line transition-transform duration-300"
            style={{
              fontSize: "12px",
              transform: hovered ? "translateX(4px)" : "translateX(0)",
            }}
          />
        </button>
      </div>
    </div>
  );
}

/* ── Main Section ── */
export default function BusinessSection() {
  const { ref: titleRef, visible: titleVisible } = useReveal(0.2);

  return (
    <section
      className="relative"
      style={{
        fontFamily: "'Noto Sans JP', sans-serif",
        background: "linear-gradient(to right, #e8ddd8, #ffffff)",
      }}
    >
      <div className="px-4 md:px-8 lg:px-12 pt-14 md:pt-20 pb-20 md:pb-32">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* ── Left: Sticky Title ── */}
          <aside className="w-full lg:w-[340px] flex-shrink-0">
            <div className="lg:sticky lg:top-28">
              <div ref={titleRef}>
                <AnimatedTitle
                  outlineText="Our"
                  filledText="Service"
                  stacked
                  delay={0}
                  labelTag="WHAT WE DO"
                  showBottomLine={false}
                  fontSize="clamp(48px, 7vw, 80px)"
                />
                {/* Bottom accent line */}
                <div
                  className="mt-7"
                  style={{
                    width: "48px",
                    height: "3px",
                    background: "#c00000",
                    transform: titleVisible ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "left center",
                    transition:
                      "transform 0.8s cubic-bezier(0.22,1,0.36,1) 0.5s",
                  }}
                />
              </div>
            </div>
          </aside>

          {/* ── Right: Staggered Cards ── */}
          <main className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {services.map((s, idx) => {
                const card = (
                  <ServiceCard
                    key={s.num}
                    num={s.num}
                    title={s.title}
                    titleEn={s.titleEn}
                    desc={s.desc}
                    idx={idx}
                  />
                );
                // 千鳥配置: 右列（偶数）を下にオフセット
                const isEven = (idx + 1) % 2 === 0;
                return (
                  <div
                    key={s.num}
                    style={{
                      marginTop: isEven ? "5rem" : "0",
                    }}
                  >
                    {card}
                  </div>
                );
              })}
            </div>
          </main>
        </div>

        {/* ── Section-level VIEW MORE ── */}
        <div
          className="flex justify-center mt-16 md:mt-20"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? "translateY(0)" : "translateY(16px)",
            transition:
              "opacity 0.7s ease 0.6s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.6s",
          }}
        >
          <ServiceViewMore />
        </div>
      </div>
    </section>
  );
}

/* ── Section CTA button ── */
function ServiceViewMore() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/service")}
      className="flex items-center gap-3 px-8 py-3.5 cursor-pointer whitespace-nowrap transition-all duration-300"
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "13px",
        fontWeight: 600,
        letterSpacing: "0.12em",
        background: "#c00000",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#a30000";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#c00000";
      }}
    >
      VIEW ALL SERVICES
      <i className="ri-arrow-right-line" style={{ fontSize: "14px" }} />
    </button>
  );
}